import type { CollectionOption } from '@/types/collection';

/**
 * Карточка варианта.
 *
 * Пользователь выбирает картинкой и подписью, а не строкой из выпадающего
 * списка: превью задаёт коллекция (изображение или цветовые свотчи), и ядро
 * не знает, что именно оно показывает.
 *
 * Заблокированный вариант остаётся видимым и объясняет причину — исчезнувшая
 * карточка выглядит как сбой, а не как правило коллекции.
 */
interface OptionCardProps {
  option: CollectionOption;
  selected: boolean;
  blockedReason: string | null;
  onToggle: () => void;
}

export function OptionCard({ option, selected, blockedReason, onToggle }: OptionCardProps) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      disabled={Boolean(blockedReason)}
      onClick={onToggle}
      className={`flex flex-col gap-1.5 rounded-xl border p-3 text-left disabled:cursor-not-allowed disabled:opacity-50 ${
        selected ? 'border-accent bg-accent-soft' : 'border-border bg-surface hover:border-border-strong'
      }`}
    >
      {option.preview?.kind === 'image' && (
        <img
          src={option.preview.src}
          alt={option.preview.alt}
          className="h-24 w-full rounded-lg object-cover"
        />
      )}

      {option.preview?.kind === 'colors' && (
        <span className="flex overflow-hidden rounded border border-border">
          {option.preview.colors.map((color) => (
            <i key={color} className="block h-6 flex-1" style={{ backgroundColor: color }} />
          ))}
        </span>
      )}

      <span className="text-sm text-text">{option.label}</span>
      {/* На заливке accent-soft третичный текст не держит контраст — берём вторичный. */}
      {option.description && (
        <span className={`text-xs ${selected ? 'text-text-2' : 'text-text-3'}`}>{option.description}</span>
      )}
      {blockedReason && <span className="text-xs text-danger">{blockedReason}</span>}
    </button>
  );
}
